<template>
  <div>
    <v-card outlined rounded="lg" class="mb-3 pa-4">
      <div class="arcade-header">
        <div class="arcade-header-text">
          <div class="orbitron-title mb-2">
            Minijuegos Arcade
          </div>

          <p class="text-subtitle-1 grey--text text--darken-1 mb-0">
            ¡Diviértete con nuestros juegos y
            <strong>obtén dinero extra</strong>
            para comprar activos!
            <br>
            <strong>RECORDATORIO</strong>:
            solo podrás jugar a dos minijuegos cada día,
            elige bien a cuáles
          </p>
        </div>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="ranking-info-btn"
              icon
              elevation="2"
              v-bind="attrs"
              v-on="on"
              @click="openRanking"
            >
              <span class="material-symbols-outlined">
                leaderboard
              </span>
            </v-btn>
          </template>

          <span>Ranking semanal de minijuegos</span>
        </v-tooltip>
      </div>
    </v-card>

    <v-container class="content-container">
      <div v-if="!activeGame">
        <div class="text-center mb-6"></div>

        <v-row>
          <v-col
            v-for="game in games"
            :key="game.id"
            cols="12"
          >
            <v-card
              outlined
              rounded="lg"
              class="pa-4 d-flex align-center hover-card"
              :class="{
                'game-disabled': game.disabled
              }"
            >
              <v-avatar
                size="80"
                rounded="lg"
                class="mr-4 grey lighten-3"
              >
                <v-img
                  v-if="game.image"
                  :src="game.image"
                  :alt="game.title"
                  cover
                >
                  <template v-slot:placeholder>
                    <v-row
                      class="fill-height ma-0"
                      align="center"
                      justify="center"
                    >
                      <v-progress-circular
                        indeterminate
                        color="primary"
                        size="20"
                      />
                    </v-row>
                  </template>
                </v-img>

                <v-icon
                  v-else
                  size="40"
                  color="primary"
                >
                  {{ game.icon }}
                </v-icon>
              </v-avatar>

              <div class="flex-grow-1">
                <h3 class="text-h6 mb-1">
                  {{ game.title }}
                </h3>

                <p class="text-body-2 mb-0 grey--text text--darken-1">
                  {{ game.description }}
                </p>
              </div>

              <v-chip
                v-if="game.disabled"
                class="coming-soon-chip"
              >
                PRÓXIMAMENTE
              </v-chip>

              <v-btn
                v-else
                color="primary"
                elevation="0"
                rounded
                class="play-btn"
                @click="activeGame = game.component"
              >
                PLAY

                <span class="material-symbols-outlined ml-1 play-icon">
                  play_arrow
                </span>
              </v-btn>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <div v-else>
        <v-btn
          text
          small
          @click="activeGame = null"
          class="btn"
        >
          <span
            class="material-symbols-outlined mr-1"
            style="font-size: 19px;"
          >
            arrow_back
          </span>

          Volver al menú
        </v-btn>

        <component :is="activeGame"
        @volver-menu="activeGame = null" />
      </div>
    </v-container>

    <v-navigation-drawer
      v-model="rankingDrawer"
      fixed
      temporary
      right
      :width="$vuetify.breakpoint.smAndDown ? '100%' : 440"
      class="ranking-drawer"
    >
      <div class="ranking-header">
        <div>
          <div class="ranking-title">
            Ranking semanal
          </div>

          <div class="ranking-subtitle">
            Mejores puntuaciones
          </div>
        </div>

        <v-btn
          icon
          @click="rankingDrawer = false"
        >
          <span class="material-symbols-outlined">
            close
          </span>
        </v-btn>
      </div>

      <v-divider />

      <div class="ranking-info">
        <div class="ranking-info-icon">
          <span class="material-symbols-outlined">
            info
          </span>
        </div>

        <div>
          <div>
            El ranking muestra la mejor puntuación obtenida
            por cada jugador en cada minijuego.
          </div>

          <div class="ranking-reset-text">
            Los récords se reinician cada lunes.
          </div>
        </div>
      </div>

      <div class="ranking-games-selector">
        <button
          v-for="game in rankingGames"
          :key="game.id"
          type="button"
          class="ranking-game-option"
          :class="{
            'ranking-game-option-active':
              selectedRankingGame === game.id
          }"
          @click="selectRankingGame(game.id)"
        >
          <v-avatar
            size="46"
            rounded="lg"
            class="ranking-game-avatar"
          >
            <v-img
              :src="game.image"
              :alt="game.name"
              cover
            />
          </v-avatar>

          <span class="ranking-game-name">
            {{ game.shortName }}
          </span>
        </button>
      </div>

      <div
        v-if="selectedRankingGameData"
        class="selected-game"
      >
        <div class="selected-game-title">
          {{ selectedRankingGameData.name }}
        </div>

        <div class="selected-game-subtitle">
          Mejor puntuación de la semana
        </div>
      </div>

      <div
        v-if="weekStart && weekEnd"
        class="ranking-week"
      >
        <span class="material-symbols-outlined">
          calendar_month
        </span>

        <span>
          {{ formattedWeek }}
        </span>
      </div>

      <div
        v-if="rankingLoading"
        class="ranking-loading"
      >
        <v-progress-circular
          indeterminate
          color="primary"
          size="38"
        />

        <div class="mt-3">
          Cargando clasificación...
        </div>
      </div>

      <v-alert
        v-else-if="rankingError"
        type="error"
        text
        class="ma-4"
      >
        {{ rankingError }}
      </v-alert>

      <div
        v-else-if="weeklyRanking.length === 0"
        class="ranking-empty"
      >
        <span class="material-symbols-outlined ranking-empty-icon">
          sports_esports
        </span>

        <div class="ranking-empty-title">
          Todavía no hay puntuaciones
        </div>

        <div class="ranking-empty-text">
          Sé el primero en conseguir un récord esta semana.
        </div>
      </div>

      <div
        v-else
        class="ranking-list"
      >
        <div
          v-for="player in weeklyRanking"
          :key="player.usuario_id"
          class="ranking-player"
          :class="{
            'ranking-player-me': isCurrentPlayer(player)
          }"
        >
          <div
            class="ranking-position"
            :class="positionClass(player.posicion)"
          >
            <span v-if="player.posicion <= 3">
              {{ medal(player.posicion) }}
            </span>

            <span v-else>
              {{ player.posicion }}
            </span>
          </div>

          <div class="ranking-player-data">
            <div class="ranking-player-name">
              <span class="ranking-player-name-text">
                {{ player.username }}
              </span>

              <span
                v-if="isCurrentPlayer(player)"
                class="ranking-you"
              >
                TÚ
              </span>
            </div>
          </div>

          <div class="ranking-points">
            <div class="ranking-score">
              {{ formatNumber(player.score) }}
            </div>

            <div class="ranking-score-label">
            {{ rankingScoreLabel }}
          </div>
          </div>
        </div>
      </div>

      <div
        v-if="!rankingLoading"
        class="ranking-refresh"
      >
        <v-btn
          text
          small
          color="primary"
          @click="loadWeeklyRanking"
        >
          <span
            class="material-symbols-outlined mr-1"
            style="font-size: 18px;"
          >
            refresh
          </span>

          Actualizar
        </v-btn>
      </div>
    </v-navigation-drawer>
  </div>
</template>

<script>
import ElectricMadMarket from "@/components/minigames/ElectricMadMarket.vue";
import FotonReboton from "@/components/minigames/FotonReboton.vue";
import Apagon from "@/components/minigames/Apagon.vue";
import Quiz from "@/components/minigames/Quiz.vue";
import GridDash from "@/components/minigames/GridDash.vue";

import axios from "@/axios";
import { useGameStore } from "@/stores/game";

export default {
  name: "MenuMiniGames",

  components: {
    ElectricMadMarket,
    FotonReboton,
    Apagon,
    Quiz,
    GridDash,
  },

  data() {
    return {
      store: useGameStore(),

      activeGame: null,

      rankingDrawer: false,
      rankingLoading: false,
      rankingError: "",
      weeklyRanking: [],
      weekStart: null,
      weekEnd: null,

      selectedRankingGame: "market",

      rankingGames: [
        {
          id: "market",
          name: "Electric Mad Market",
          shortName: "Mad Market",
          image: "/minigames/market/portada.jpg",
        },
        {
          id: "foton",
          name: "Fotón Rebotón",
          shortName: "Fotón",
          image: "/minigames/foton/portada.jpg",
        },
        {
          id: "apagon",
          name: "Apagón Masivo",
          shortName: "Apagón",
          image: "/minigames/apagon/portada.jpg",
        },
        /*{
          id: "quiz_market",
          name: "Market Quiz",
          shortName: "Quiz",
          image: "/minigames/quiz/miniatura.png",
        },
        {
          id: "grid_dash",
          name: "Grid Dash",
          shortName: "Grid Dash",
          image: "/minigames/gridDash/miniatura.png",
        },*/
      ],

      games: [
        {
          id: 1,
          title: "Electric Mad Market",
          description:
            "Participa en un loco día de mercado eléctrico, oferta para cumplir tu previsión y llega al P48 sin desviarte.",
          icon: "mdi-lightning-bolt",
          image: "/minigames/market/portada.jpg",
          component: "ElectricMadMarket",
        },
        {
          id: 2,
          title: "Fotón Rebotón",
          description:
            "Domina al fotón y consigue producir lo máximo posible en tu planta fotovoltaica.",
          icon: "mdi-atom",
          image: "/minigames/foton/portada.jpg",
          component: "FotonReboton",
        },
        {
          id: 3,
          title: "Apagón Masivo",
          description:
            "¡Ha habido un apagón en la ciudad! Reestablece la red para que la casa de Simulito vuelva a tener luz.",
          icon: "mdi-power-plug-off",
          image: "/minigames/apagon/portada.jpg",
          component: "Apagon",
        },
        {
          id: 4,
          title: "Market Quiz",
          description:
            "Pon a prueba tus conocimientos sobre el mercado eléctrico. Responde preguntas en 1 minuto y gana dinero extra.",
          icon: "mdi-help-circle",
          image: "/minigames/quiz/miniatura.png",
          component: "Quiz",
          disabled: true,
        },
        {
          id: 5,
          title: "Grid Dash",
          description:
            "Corre por el mercado eléctrico y recoge MWh rentables.",
          icon: "mdi-help-circle",
          image: "/minigames/gridDash/miniatura.png",
          component: "GridDash",
          disabled: true,
        },
      ],
    };
  },

  computed: {
  rankingScoreLabel() {
  if (this.selectedRankingGame === "foton") {
    return "MWh";
  }

  return "puntos";
},
    selectedRankingGameData() {
      return (
        this.rankingGames.find(
          game => game.id === this.selectedRankingGame
        ) || null
      );
    },

    currentLigaId() {
      try {
        return this.store.resolveLigaId();
      } catch (e) {
        return null;
      }
    },

    currentUserId() {
      return this.store.authUser?.id != null
        ? String(this.store.authUser.id)
        : null;
    },

    formattedWeek() {
      if (!this.weekStart || !this.weekEnd) {
        return "";
      }

      const start = new Date(this.weekStart);
      const end = new Date(this.weekEnd);

      end.setDate(end.getDate() - 1);

      const startText = start.toLocaleDateString(
        "es-ES",
        {
          day: "2-digit",
          month: "2-digit",
        }
      );

      const endText = end.toLocaleDateString(
        "es-ES",
        {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }
      );

      return `${startText} - ${endText}`;
    },
  },

  methods: {
    async openRanking() {
      this.rankingDrawer = true;
      await this.loadWeeklyRanking();
    },

    async selectRankingGame(gameId) {
      if (this.selectedRankingGame === gameId) {
        return;
      }

      this.selectedRankingGame = gameId;
      await this.loadWeeklyRanking();
    },

    async loadWeeklyRanking() {
      const ligaId = this.currentLigaId;

      if (!ligaId) {
        this.weeklyRanking = [];
        this.rankingError =
          "No se ha podido identificar la liga activa.";
        return;
      }

      this.rankingLoading = true;
      this.rankingError = "";

      try {
        const response = await axios.get(
          "/api/rankings/minigames-weekly",
          {
            params: {
              liga_id: ligaId,
              game_id: this.selectedRankingGame,
            },
          }
        );

        const payload = response?.data;

        const data =
          payload?.status === "success"
            ? payload.data
            : payload;

        this.weeklyRanking =
          Array.isArray(data?.ranking)
            ? data.ranking
            : [];

        this.weekStart =
          data?.week_start || null;

        this.weekEnd =
          data?.week_end || null;
      } catch (error) {
        console.error(
          "Error cargando ranking semanal:",
          error
        );

        this.weeklyRanking = [];

        this.rankingError =
          error?.response?.data?.message ||
          error?.response?.data?.data?.message ||
          error?.message ||
          "No se pudo cargar el ranking.";
      } finally {
        this.rankingLoading = false;
      }
    },

    isCurrentPlayer(player) {
      return (
        this.currentUserId &&
        String(player.usuario_id) ===
          String(this.currentUserId)
      );
    },

    medal(position) {
      if (position === 1) {
        return "🥇";
      }

      if (position === 2) {
        return "🥈";
      }

      if (position === 3) {
        return "🥉";
      }

      return position;
    },

    positionClass(position) {
      return {
        "ranking-first": position === 1,
        "ranking-second": position === 2,
        "ranking-third": position === 3,
      };
    },

    formatNumber(value) {
      return Number(value || 0).toLocaleString(
        "es-ES"
      );
    },
  },
};
</script>

<style scoped>
.content-container {
  max-width: 1200px;
  margin: 0 auto;
}

.arcade-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.arcade-header-text {
  flex: 1;
  min-width: 0;
}

.ranking-info-btn {
  flex: 0 0 auto;
  width: 46px !important;
  height: 46px !important;
  background: #f3cd33 !important;
  color: #222 !important;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.ranking-info-btn:hover {
  transform: scale(1.06);
  box-shadow:
    0 5px 14px rgba(0, 0, 0, 0.18)
    !important;
}

.ranking-info-btn .material-symbols-outlined {
  font-size: 25px;
}

.hover-card {
  transition:
    transform 0.2s ease-in-out,
    box-shadow 0.2s;
  cursor: pointer;
}

.hover-card:hover {
  transform: translateY(-2px);
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.1)
    !important;
}

.text-body-2 {
  line-height: 1.4;
}

.v-avatar .v-image {
  width: 100%;
  height: 100%;
}

.orbitron-title {
  font-family: "Orbitron", sans-serif !important;
  font-size: 20px;
  font-weight: 600;
}

.btn {
  font-family: "Orbitron", sans-serif !important;
  background-color: #f3cd33;
  font-size: 19px !important;
}

.play-icon {
  font-size: 24px;
  line-height: 0.5;
}

.ranking-drawer {
  background: #f7f8f8 !important;
}

.ranking-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 22px 20px 18px;
  background: white;
}

.ranking-title {
  font-family: "Orbitron", sans-serif !important;
  font-size: 21px;
  font-weight: 700;
  color: #31635f;
}

.ranking-subtitle {
  margin-top: 3px;
  font-size: 13px;
  color: #777;
}

.ranking-info {
  display: flex;
  gap: 10px;
  margin: 16px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(49, 99, 95, 0.09);
  font-size: 13px;
  line-height: 1.4;
}

.ranking-info-icon {
  flex: 0 0 auto;
  color: #31635f;
}

.ranking-info-icon .material-symbols-outlined {
  font-size: 21px;
}

.ranking-reset-text {
  margin-top: 4px;
  font-weight: 700;
  color: #31635f;
}

.ranking-games-selector {
  display: flex;
  gap: 8px;
  padding: 2px 16px 16px;
  overflow-x: auto;
  scrollbar-width: thin;
}

.ranking-game-option {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  width: 72px;
  min-height: 78px;
  padding: 7px 5px;
  border: 2px solid transparent;
  border-radius: 13px;
  background: white;
  cursor: pointer;
  outline: none;
  transition:
    border-color 0.2s ease,
    background 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.ranking-game-option:hover {
  transform: translateY(-2px);
  box-shadow:
    0 3px 10px rgba(0, 0, 0, 0.09);
}

.ranking-game-option-active {
  border-color: #31635f;
  background: rgba(49, 99, 95, 0.07);
}

.ranking-game-avatar {
  flex: 0 0 auto;
}

.ranking-game-name {
  width: 100%;
  font-size: 9px;
  font-weight: 600;
  line-height: 1.15;
  text-align: center;
  color: #555;
}

.ranking-game-option-active .ranking-game-name {
  color: #31635f;
  font-weight: 700;
}

.selected-game {
  margin: 0 16px 10px;
  text-align: center;
}

.selected-game-title {
  font-family: "Orbitron", sans-serif !important;
  font-size: 17px;
  font-weight: 700;
  color: #31635f;
}

.selected-game-subtitle {
  margin-top: 3px;
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #777;
}

.ranking-week {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 7px;
  margin: 4px 16px 18px;
  font-size: 13px;
  font-weight: 600;
  color: #666;
}

.ranking-week .material-symbols-outlined {
  font-size: 18px;
}

.ranking-list {
  padding: 0 14px;
}

.ranking-player {
  display: flex;
  align-items: center;
  min-height: 72px;
  margin-bottom: 10px;
  padding: 10px 12px;
  border-radius: 14px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.07);
  box-shadow:
    0 2px 7px rgba(0, 0, 0, 0.05);
}

.ranking-player-me {
  border: 2px solid #31635f;
  background: rgba(49, 99, 95, 0.05);
}

.ranking-position {
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  margin-right: 11px;
  border-radius: 50%;
  background: #eeeeee;
  font-family: "Orbitron", sans-serif !important;
  font-weight: 700;
}

.ranking-first,
.ranking-second,
.ranking-third {
  font-size: 24px;
  background: transparent;
}

.ranking-player-data {
  flex: 1;
  min-width: 0;
}

.ranking-player-name {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-weight: 700;
}

.ranking-player-name-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ranking-you {
  flex: 0 0 auto;
  padding: 2px 6px;
  border-radius: 5px;
  background: #31635f;
  color: white;
  font-size: 9px;
  font-weight: 700;
}

.ranking-points {
  flex: 0 0 auto;
  margin-left: 8px;
  text-align: right;
}

.ranking-score {
  font-family: "Orbitron", sans-serif !important;
  font-size: 16px;
  font-weight: 700;
  color: #31635f;
}

.ranking-score-label {
  font-size: 10px;
  color: #888;
  text-transform: uppercase;
}

.ranking-loading {
  padding: 60px 20px;
  text-align: center;
  color: #777;
}

.ranking-empty {
  padding: 55px 25px;
  text-align: center;
}

.ranking-empty-icon {
  font-size: 50px;
  color: #bdbdbd;
}

.ranking-empty-title {
  margin-top: 10px;
  font-family: "Orbitron", sans-serif !important;
  font-size: 16px;
  font-weight: 600;
}

.ranking-empty-text {
  margin-top: 5px;
  color: #888;
  font-size: 13px;
}

.ranking-refresh {
  display: flex;
  justify-content: center;
  padding: 10px 0 20px;
}
.game-disabled {
  opacity: 0.8;
  filter: grayscale(0.8);
  cursor: not-allowed !important;
  background: #f3f3f3 !important;
}

.game-disabled:hover {
  transform: none !important;
  box-shadow: none !important;
}

.coming-soon-chip {
  flex: 0 0 auto;
  font-family: "Orbitron", sans-serif !important;
  font-size: 10px !important;
  font-weight: 800 !important;
  letter-spacing: 0.5px;
  color: #666 !important;
  background: #dddddd !important;
  border: 1px solid #c5c5c5 !important;
}
.play-btn {
  font-family: orbitron;
  transition:
    transform 0.2s ease,
    background-color 0.2s ease,
    color 0.2s ease;
}

.play-btn:hover {
  transform: scale(1.08);
  background: linear-gradient(
    90deg,
    #22c55e,
    #38bdf8
  ) !important;
  color: white !important;
}

@media (max-width: 600px) {
  .arcade-header {
    align-items: flex-start;
    gap: 8px;
  }

  .ranking-info-btn {
    width: 40px !important;
    height: 40px !important;
  }

  .ranking-info-btn .material-symbols-outlined {
    font-size: 22px;
  }

  .ranking-title {
    font-size: 18px;
  }

  .ranking-games-selector {
    padding-left: 12px;
    padding-right: 12px;
  }

  .ranking-game-option {
    width: 68px;
  }

  .selected-game-title {
    font-size: 15px;
  }

  .ranking-player {
    min-height: 65px;
  }

  .ranking-score {
    font-size: 14px;
  }
}
</style>